import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import usePageMeta from "../hooks/usePageMeta";
import Breadcrumbs from "../components/Breadcrumbs";
import QuickQuoteStrip from "../components/QuickQuoteStrip";
import { getArticleBySlug, ARTICLES } from "../blog";

const renderSection = (s, i) => {
  if (s.type === "h2") {
    return (
      <h2
        key={i}
        className="font-serif-display text-2xl md:text-[34px] leading-[1.2] text-[#1C110C] mt-12 md:mt-16 mb-5"
      >
        {s.text}
      </h2>
    );
  }
  if (s.type === "h3") {
    return (
      <h3
        key={i}
        className="font-serif-display text-xl md:text-2xl leading-[1.25] text-[#1C110C] mt-8 mb-3"
      >
        {s.text}
      </h3>
    );
  }
  if (s.type === "ul") {
    return (
      <ul
        key={i}
        className="my-6 space-y-2.5 text-[#3E2A22] leading-[1.85] text-base md:text-[17px]"
      >
        {s.items.map((it, j) => (
          <li
            key={j}
            className="flex items-baseline gap-3"
          >
            <span className="text-[#D4A963] text-sm">◦</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    );
  }
  // paragraph (with optional inline link at the end)
  return (
    <p
      key={i}
      className="my-5 text-[#3E2A22] leading-[1.85] text-base md:text-[17px]"
    >
      {s.text}
      {s.link && (
        <>
          {" "}
          <Link
            to={s.link.to}
            className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
          >
            {s.link.text}
          </Link>
          .
        </>
      )}
    </p>
  );
};

export default function BlogArticle() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  usePageMeta({
    title: article?.metaTitle,
    description: article?.metaDescription,
    canonical: article
      ? `https://timefurniture.ae/blog/${article.slug}`
      : undefined,
    ogImage: article?.image,
    ogType: "article",
    jsonLd: article
      ? {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: article.title,
          description: article.metaDescription,
          datePublished: article.date,
          author: { "@id": "https://timefurniture.ae/#organization" },
          publisher: { "@id": "https://timefurniture.ae/#organization" },
          image: `https://timefurniture.ae${article.image}`,
          url: `https://timefurniture.ae/blog/${article.slug}`,
          mainEntityOfPage: `https://timefurniture.ae/blog/${article.slug}`,
        }
      : undefined,
  });

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);

  return (
    <div data-testid="blog-article-page" className="bg-[#FAF5EC]">
      <Breadcrumbs
        items={[
          { label: "Home", to: "/" },
          { label: "Blog", to: "/blog" },
          { label: article.title },
        ]}
      />

      <article className="max-w-[820px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-20">
        {/* Header */}
        <header className="mb-12">
          <p className="eyebrow mb-5">
            {article.category} · {article.readTime}
          </p>
          <h1
            data-testid="article-h1"
            className="font-serif-display text-4xl md:text-5xl lg:text-[56px] leading-[1.05] tracking-tight text-[#1C110C]"
          >
            {article.title}
          </h1>
          <p className="mt-6 text-sm text-[#7A6B5F]">
            By Time Furniture · Published{" "}
            {new Date(article.date).toLocaleDateString("en-GB", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </header>

        {/* Hero image */}
        <div className="aspect-[16/9] bg-[#1C110C] mb-12 overflow-hidden">
          <img loading="lazy"
            src={article.image}
            alt={article.imageAlt}
            className="w-full h-full object-cover"
          />
        </div>

        {/* Body */}
        <div data-testid="article-body">
          {article.sections.map(renderSection)}
        </div>
      </article>

      {/* CTA strip */}
      <QuickQuoteStrip
        testid="article-quick-quote"
        prefilled={`Hi Time Furniture, I read your article "${article.title}" and would like a curtain quote. My window sizes are: `}
        variant="burgundy"
      />

      {/* Related */}
      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <p className="eyebrow mb-8">Continue reading</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            {related.map((a) => (
              <Link
                key={a.slug}
                to={`/blog/${a.slug}`}
                data-testid={`article-related-${a.slug}`}
                className="group block"
              >
                <div className="relative overflow-hidden bg-[#1C110C] aspect-[16/9] mb-5">
                  <img
                    src={a.image}
                    alt={a.imageAlt}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  />
                </div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963] mb-3">
                  {a.category} · {a.readTime}
                </p>
                <h3 className="font-serif-display text-2xl leading-[1.2] text-[#1C110C] group-hover:text-[#6E1F2C] transition-colors">
                  {a.title}
                </h3>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
